(() => {
  if (window.DWILUgcVideoAssetLoaded) {
    window.DWILUgcVideoInitAll?.();
    return;
  }

  window.DWILUgcVideoAssetLoaded = true;

  const initializedSections = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initSection = (root) => {
    if (!root || initializedSections.has(root)) return;

    const track = root.querySelector('[data-dwil-ugc-track]');
    const navButtons = Array.from(root.querySelectorAll('[data-dwil-ugc-nav]'));
    const cards = Array.from(root.querySelectorAll('[data-dwil-ugc-card]'));
    const modal = root.querySelector('[data-dwil-ugc-modal]');
    const modalShell = modal?.querySelector('[data-dwil-ugc-modal-shell]');
    const modalStage = modal?.querySelector('[data-dwil-ugc-modal-stage]');
    const modalBackdrop = modal?.querySelector('[data-dwil-ugc-modal-backdrop]');
    const closeButton = modal?.querySelector('[data-dwil-ugc-modal-close]');
    const cleanups = [];
    let scrollFrame = null;
    let activeTrigger = null;
    let activeVideo = null;
    let activeContent = null;

    const on = (target, eventName, handler, options) => {
      if (!target) return;
      target.addEventListener(eventName, handler, options);
      cleanups.push(() => target.removeEventListener(eventName, handler, options));
    };

    const isModalOpen = () => Boolean(modal?.open || modal?.hasAttribute('open'));

    const getNumericCssVariable = (name, fallback) => {
      const value = Number.parseFloat(window.getComputedStyle(root).getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    };

    const fitModalToViewport = () => {
      if (!activeContent) return;

      const viewportWidth = window.visualViewport?.width || window.innerWidth;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const gap = getNumericCssVariable('--dwil-ugc-modal-viewport-gap', 24);
      const isLandscape = activeContent.classList.contains('is-landscape');
      const ratio = isLandscape ? 16 / 9 : 9 / 16;
      const maxWidth = getNumericCssVariable(
        isLandscape ? '--dwil-ugc-modal-landscape-width' : '--dwil-ugc-modal-portrait-width',
        isLandscape ? 1120 : 480
      );
      const availableWidth = Math.max(1, viewportWidth - (gap * 2));
      const availableHeight = Math.max(1, viewportHeight - (gap * 2));
      const initialWidth = Math.min(maxWidth, availableWidth);

      activeContent.style.width = `${Math.floor(initialWidth)}px`;
      activeContent.style.height = 'auto';

      const product = activeContent.querySelector('.dwil-ugc-video__modal-product');
      const contentStyles = window.getComputedStyle(activeContent);
      const contentGap = Number.parseFloat(contentStyles.rowGap || contentStyles.gap || '0') || 0;
      const productHeight = product?.getBoundingClientRect().height || 0;
      const availableVideoHeight = Math.max(1, availableHeight - productHeight - (product ? contentGap : 0));
      const width = Math.min(initialWidth, availableVideoHeight * ratio);

      activeContent.style.width = `${Math.floor(width)}px`;
    };

    const clearModal = () => {
      if (activeVideo) {
        activeVideo.pause();
        activeVideo.removeAttribute('src');
        activeVideo.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        activeVideo.load();
      }

      activeVideo = null;
      activeContent = null;
      modalStage?.replaceChildren();

      const triggerToRestore = activeTrigger;
      activeTrigger = null;
      if (triggerToRestore?.isConnected) {
        window.requestAnimationFrame(() => triggerToRestore.focus({ preventScroll: true }));
      }
    };

    const closeModal = () => {
      if (!modal || !isModalOpen()) return;

      if (typeof modal.close === 'function') {
        modal.close();
      } else {
        modal.removeAttribute('open');
        clearModal();
      }
    };

    const setVideoOrientation = (video, content) => {
      if (!video || !content) return;
      content.classList.toggle('is-landscape', video.videoWidth > video.videoHeight);
      fitModalToViewport();
    };

    const openModal = (card, trigger) => {
      const template = card?.querySelector('[data-dwil-ugc-modal-template]');
      if (!modal || !modalStage || !template) return;

      if (isModalOpen()) closeModal();

      activeTrigger = trigger || card.querySelector('[data-dwil-ugc-play]');
      modalStage.replaceChildren(template.content.cloneNode(true));

      const content = modalStage.querySelector('[data-dwil-ugc-modal-content]');
      activeContent = content;
      activeVideo = modalStage.querySelector('[data-dwil-ugc-modal-video]');
      fitModalToViewport();

      if (activeVideo && content) {
        if (activeVideo.readyState >= 1) {
          setVideoOrientation(activeVideo, content);
        } else {
          activeVideo.addEventListener(
            'loadedmetadata',
            () => setVideoOrientation(activeVideo, content),
            { once: true }
          );
        }
      }

      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }

      window.requestAnimationFrame(() => {
        fitModalToViewport();
        closeButton?.focus({ preventScroll: true });
      });
      activeVideo?.play().catch(() => {});
    };

    cards.forEach((card) => {
      const playButton = card.querySelector('[data-dwil-ugc-play]');
      if (!playButton) return;

      on(playButton, 'click', () => openModal(card, playButton));
      on(card, 'click', (event) => {
        if (event.target.closest('a, button')) return;
        openModal(card, playButton);
      });
    });

    if (modal) {
      on(closeButton, 'click', closeModal);
      on(modalBackdrop, 'click', closeModal);
      on(modal, 'close', clearModal);
      on(modal, 'cancel', (event) => {
        event.preventDefault();
        closeModal();
      });
      on(modal, 'click', (event) => {
        if (event.target === modal || event.target === modalShell || event.target === modalStage) closeModal();
      });
      on(modal, 'keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeModal();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = Array.from(
          modal.querySelectorAll('button:not([disabled]), a[href], video[controls], [tabindex]:not([tabindex="-1"])')
        ).filter((element) => !element.hasAttribute('hidden') && element.getClientRects().length > 0);

        if (!focusable.length) {
          event.preventDefault();
          closeButton?.focus();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      on(window, 'resize', fitModalToViewport, { passive: true });
      on(window.visualViewport, 'resize', fitModalToViewport, { passive: true });
    }

    if (track && navButtons.length) {
      const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth - 1);
      const isScrollable = () => track.scrollWidth > track.clientWidth + 2;
      const getScrollStep = () => {
        const firstCard = cards[0];
        if (!firstCard) return track.clientWidth * 0.85;

        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return firstCard.getBoundingClientRect().width + gap;
      };

      const setButtonState = () => {
        const scrollable = isScrollable();
        const maxScroll = getMaxScroll();
        const atStart = track.scrollLeft <= 1;
        const atEnd = track.scrollLeft >= maxScroll;

        navButtons.forEach((button) => {
          const direction = Number(button.dataset.dwilUgcNav || 1);
          button.hidden = !scrollable;
          button.disabled = !scrollable || (direction < 0 ? atStart : atEnd);
          button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
        });
      };

      navButtons.forEach((button) => {
        on(button, 'click', () => {
          if (button.disabled) return;

          track.scrollBy({
            left: Number(button.dataset.dwilUgcNav || 1) * getScrollStep(),
            behavior: reduceMotion ? 'auto' : 'smooth'
          });
        });
      });

      on(
        track,
        'scroll',
        () => {
          if (scrollFrame) return;
          scrollFrame = window.requestAnimationFrame(() => {
            scrollFrame = null;
            setButtonState();
          });
        },
        { passive: true }
      );

      on(track, 'keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        event.preventDefault();
        track.scrollBy({
          left: (event.key === 'ArrowRight' ? 1 : -1) * getScrollStep(),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      });

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(setButtonState);
        resizeObserver.observe(track);
        cleanups.push(() => resizeObserver.disconnect());
      } else {
        on(window, 'resize', setButtonState);
      }

      window.requestAnimationFrame(setButtonState);
    }

    initializedSections.set(root, () => {
      if (isModalOpen()) closeModal();
      clearModal();
      cleanups.forEach((removeListener) => removeListener());
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      initializedSections.delete(root);
    });
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-dwil-ugc-section]')) initSection(scope);
    scope.querySelectorAll?.('[data-dwil-ugc-section]').forEach(initSection);
  };

  window.DWILUgcVideoInitAll = initAll;

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target?.matches?.('[data-dwil-ugc-section]')
      ? event.target
      : event.target?.querySelector?.('[data-dwil-ugc-section]');
    initializedSections.get(root)?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const selectedCard = event.target?.closest?.('[data-dwil-ugc-card]');
    const root = selectedCard?.closest?.('[data-dwil-ugc-section]');
    const track = root?.querySelector('[data-dwil-ugc-track]');
    if (!selectedCard || !track) return;

    const targetLeft = selectedCard.offsetLeft - ((track.clientWidth - selectedCard.clientWidth) / 2);
    track.scrollTo({ left: Math.max(0, targetLeft), behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
})();
